import { useState, useEffect, useCallback } from 'react';
import { useSmartCartApi } from '@app/react-common/api/useSmartCartApi';
import { useAppContext } from '@app/react-common/AppContext';

interface SmartCartAdminSettings {
  id: number;
  name: string;
  description?: string | null;
  isLive?: boolean;
}

export interface SmartCart {
  id: number;
  name: string;
  description?: string;
  isActive?: boolean;
}

const useSmartCarts = (shouldFetch: boolean = true) => {
  const [smartCarts, setSmartCarts] = useState<SmartCart[]>([]);
  const [loading, setLoading] = useState<boolean>(shouldFetch);
  const { showToast } = useAppContext();

  const failureHandler = useCallback(async () => {
    showToast({
      message: `Failed to load Smart Carts. Please contact support if the problem persists.`,
      mode: 'danger',
    });
    setLoading(false);
  }, [showToast]);

  const smartCartApi = useSmartCartApi(failureHandler);

  const fetchSmartCarts = async () => {
    try {
      setLoading(true);
      const response = await smartCartApi.get('/smart-cart/admin');
      const carts: SmartCart[] = (response.data || []).map((item: SmartCartAdminSettings) => ({
        id: item.id,
        name: item.name || `Smart Cart ${item.id}`,
        description: item.description ?? undefined,
        isActive: item.isLive,
      }));

      setSmartCarts(carts);
    } catch (error) {
      console.error('Error fetching smart carts:', error);
      setSmartCarts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchSmartCarts();
    } else {
      setLoading(false);
    }
  }, [shouldFetch]);

  return {
    smartCarts,
    loading,
  };
};

export default useSmartCarts;
